import {
  CreateTemplateCategoryInput,
  TemplateCategory,
  UpdateTemplateCategoryInput
} from '@/types/templateCategoryTypes';
import {
  GetTokenWithPopupOptions,
  PopupConfigOptions
} from '@auth0/auth0-react';

// Define a more precise type for the getAccessTokenSilently function
type GetAccessTokenSilentlyType = (options?: any) => Promise<string>;

// Update the type for getAccessTokenWithPopup
type GetAccessTokenWithPopupType = (
  options?: GetTokenWithPopupOptions,
  config?: PopupConfigOptions
) => Promise<string | undefined>;

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
const TEMPLATE_CATEGORIES_ENDPOINT = `${API_BASE_URL}/template-categories`;

/**
 * Helper function to acquire Auth0 access token.
 * Tries silently, then falls back to popup.
 */
const getAuthToken = async (
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<string> => {
  const authParams = {
    authorizationParams: {
      audience: import.meta.env.VITE_API_AUTH0_AUDIENCE
      // scope: "read:template_categories write:template_categories" // Add specific scopes if needed
    }
  };
  try {
    return await getAccessTokenSilently(authParams);
  } catch (error: any) {
    console.warn(
      'Silent token acquisition failed for TemplateCategoryService:',
      error
    );
    if (
      error.error === 'consent_required' ||
      error.error === 'login_required' ||
      error.error === 'interaction_required'
    ) {
      console.log(
        'Falling back to interactive token acquisition for TemplateCategoryService...'
      );
      const token = await getAccessTokenWithPopup(authParams);
      if (!token) {
        throw new Error(
          'Interactive token acquisition failed to return a token.'
        );
      }
      return token;
    }
    throw error; // Rethrow other errors
  }
};

/**
 * Creates a new template category.
 * @param input - The data for the new template category.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The created template category.
 */
export const createTemplateCategory = async (
  input: CreateTemplateCategoryInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateCategory> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(`${TEMPLATE_CATEGORIES_ENDPOINT}/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    },
    body: JSON.stringify(input)
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error creating template category:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to create template category. Status: ${response.status}`
    );
  }
  return (await response.json()) as TemplateCategory;
};

/**
 * Retrieves all template categories for the user's company.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns A list of template categories.
 */
export const getAllTemplateCategories = async (
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateCategory[]> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(TEMPLATE_CATEGORIES_ENDPOINT, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'X-User-Id': userId
    }
  });

  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error('Error fetching all template categories:', errorData);
    throw new Error(
      errorData.message ||
        `Failed to fetch template categories. Status: ${response.status}`
    );
  }
  return (await response.json()) as TemplateCategory[];
};

/**
 * Retrieves a specific template category by its ID.
 * @param categoryId - The ID of the template category to retrieve.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The requested template category or null if not found.
 */
export const getTemplateCategoryById = async (
  categoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateCategory | null> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${TEMPLATE_CATEGORIES_ENDPOINT}/${categoryId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    }
  );

  if (response.status === 404) {
    console.warn(`Template category with ID '${categoryId}' not found.`);
    return null;
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error fetching template category with ID ${categoryId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to fetch template category. Status: ${response.status}`
    );
  }
  return (await response.json()) as TemplateCategory;
};

/**
 * Updates an existing template category.
 * @param categoryId - The ID of the template category to update.
 * @param input - The data to update the template category with.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns The updated template category.
 */
export const updateTemplateCategory = async (
  categoryId: number, // Often passed separately for the URL
  input: UpdateTemplateCategoryInput,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<TemplateCategory> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  // Ensure the ID in the input matches the categoryId from the path, if present in input
  if (input.id && input.id !== categoryId) {
    console.warn(
      'Mismatch between categoryId in path and input DTO. Using path ID.'
    );
  }
  const payload = { ...input, id: categoryId }; // Ensure ID is part of payload if backend expects it

  const response = await fetch(
    `${TEMPLATE_CATEGORIES_ENDPOINT}/${categoryId}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      },
      body: JSON.stringify(payload) // Send the payload which includes the ID
    }
  );

  if (response.status === 404) {
    const errorMsg = `Template category with ID '${categoryId}' not found for update.`;
    console.error(errorMsg);
    throw new Error(errorMsg); // Or return null depending on desired behavior
  }
  if (!response.ok) {
    const errorData = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    console.error(
      `Error updating template category with ID ${categoryId}:`,
      errorData
    );
    throw new Error(
      errorData.message ||
        `Failed to update template category. Status: ${response.status}`
    );
  }
  return (await response.json()) as TemplateCategory;
};

/**
 * Deletes a template category by its ID.
 * @param categoryId - The ID of the template category to delete.
 * @param userId - The ID of the user performing the action (for X-User-Id header).
 * @param getAccessTokenSilently - Auth0's getAccessTokenSilently function.
 * @param getAccessTokenWithPopup - Auth0's getAccessTokenWithPopup function.
 * @returns True if deletion was successful (204 No Content), false otherwise.
 */
export const deleteTemplateCategory = async (
  categoryId: number,
  userId: string,
  getAccessTokenSilently: GetAccessTokenSilentlyType,
  getAccessTokenWithPopup: GetAccessTokenWithPopupType
): Promise<boolean> => {
  const accessToken = await getAuthToken(
    getAccessTokenSilently,
    getAccessTokenWithPopup
  );

  const response = await fetch(
    `${TEMPLATE_CATEGORIES_ENDPOINT}/${categoryId}`,
    {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'X-User-Id': userId
      }
    }
  );

  if (response.status === 204) {
    return true; // Successfully deleted
  }
  if (response.status === 404) {
    console.warn(
      `Template category with ID '${categoryId}' not found for deletion.`
    );
    return false; // Or throw new Error(...)
  }
  if (!response.ok) {
    // For DELETE, response might not have a JSON body on error, or it might.
    let errorDetail = response.statusText;
    try {
      const errorData = await response.json();
      errorDetail = errorData.message || errorDetail;
    } catch (e) {
      // Ignore if parsing fails, use statusText
    }
    console.error(
      `Error deleting template category with ID ${categoryId}:`,
      errorDetail
    );
    throw new Error(
      `Failed to delete template category. Status: ${response.status}, Detail: ${errorDetail}`
    );
  }
  return false; // Should ideally not be reached if !response.ok throws
};
